import { ErrorHandler, Injectable } from '@angular/core'
import { faro } from '@grafana/faro-web-sdk'

const LazyChunkReloadStorageKey = 'ewc.lazy-chunk-reload-attempted-at'
const LazyChunkReloadRetryMs = 5 * 60 * 1000
const LazyChunkErrorPatterns = [
    /Failed to fetch dynamically imported module/i,
    /Importing a module script failed/i,
    /error loading dynamically imported module/i,
    /Loading chunk .+ failed/i
]

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
    handleError(error: unknown) {
        const actualError = this.unwrapError(error)

        if (actualError instanceof Error) {
            faro.api?.pushError(actualError)
        }

        if (this.isLazyChunkLoadError(actualError)) {
            console.error(error)
            this.reloadOnceAfterLazyChunkError()
            return
        }

        console.error(error)
    }

    private unwrapError(error: unknown): unknown {
        if (error && typeof error === 'object' && 'rejection' in error) {
            return this.unwrapError((error as { rejection: unknown }).rejection)
        }

        return error
    }

    private isLazyChunkLoadError(error: unknown): boolean {
        const message = this.getErrorMessage(error)

        return LazyChunkErrorPatterns.some(pattern => pattern.test(message))
    }

    private getErrorMessage(error: unknown): string {
        if (error instanceof Error) {
            return error.message
        }

        if (typeof error === 'string') {
            return error
        }

        if (error && typeof error === 'object' && 'message' in error) {
            const message = (error as { message: unknown }).message
            return typeof message === 'string' ? message : ''
        }

        return ''
    }

    private reloadOnceAfterLazyChunkError(): void {
        if (typeof window === 'undefined' || !window.sessionStorage) {
            return
        }

        const now = Date.now()
        const previousAttemptAt = Number(window.sessionStorage.getItem(LazyChunkReloadStorageKey) || 0)

        if (previousAttemptAt && now - previousAttemptAt < LazyChunkReloadRetryMs) {
            return
        }

        window.sessionStorage.setItem(LazyChunkReloadStorageKey, String(now))
        window.location.reload()
    }
}
